/**
 * A Possible Root Cause bullet can point at another section, authored inline in
 * the sheet (and stored verbatim in the DB) as
 *
 *   [[nav:<Section>:<tab?>|<label>]]
 *
 * e.g. "[[nav:Proteins:causes|Protein Status]]" links to the Proteins section
 * AND opens its Possible Root Causes tab; "[[nav:RBC|Blood Oxygen & Red Blood
 * Cell Function]]" just scrolls to RBC, which has no Causes tab.
 *
 * Like the "[Hover text: …]" annotation next door in utils/hoverText, the token
 * is markup and never body copy — no surface should print the literal
 * "[[nav:…]]". Each surface picks its treatment:
 *
 *   - the on-screen tab   → a clickable link (further-considerations-section)
 *   - plain-text surfaces → the label alone, since a note a practitioner pastes
 *     elsewhere and a raw-data export handed to a model can't be clicked
 *
 * The parsing regexes live here rather than in the component so the renderer
 * and the plain-text surfaces can never disagree about what a token is.
 */

// Split keeps the token (capture group) so the renderer can walk text and
// tokens in order. Global, which is safe for String.split — it does not consult
// lastIndex the way .test()/.exec() do.
export const NAV_TOKEN_SPLIT = /(\[\[nav:[^\]]+\]\])/g;

// Anchored and NON-global, for identifying one part produced by that split.
// Section stops at ':' so the optional tab can follow; label runs to the ']]'.
export const NAV_TOKEN_PARSE = /^\[\[nav:([^:|\]]+)(?::([^|\]]+))?\|([^\]]+)\]\]$/;

// Its own instance, because a global regex used with .replace() advances
// lastIndex and would skip matches on the next call if shared.
const NAV_TOKEN_GLOBAL = /\[\[nav:[^:|\]]+(?::[^|\]]+)?\|([^\]]+)\]\]/g;

/** True when a bullet carries at least one cross-section pointer. */
export function hasNavToken(item: string): boolean {
  return !!item && item.indexOf('[[nav:') !== -1;
}

/**
 * The bullet with each pointer reduced to its visible label, for surfaces that
 * are plain text and cannot link — the Notes digest and the AI raw-data export.
 * Keeps the sentence readable ("… see the Protein Status section") rather than
 * leaking the markup or dropping the section name the sentence depends on.
 */
export function stripNavTokens(item: string): string {
  if (!hasNavToken(item)) return item || '';
  NAV_TOKEN_GLOBAL.lastIndex = 0;
  return item.replace(NAV_TOKEN_GLOBAL, (_match, label: string) => label);
}
